if (!customElements.get("formula-faq-accordion")) {
  class FormulaFaqAccordion extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.animations = new Map();
      this.states = new WeakMap();
      this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);

      this.addEventListener("click", this.handleClick);
      this.addEventListener("keydown", this.handleKeydown);

      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener("change", this.handleMotionChange);
      } else {
        this.motionQuery.addListener(this.handleMotionChange);
      }
    }

    disconnectedCallback() {
      this.settleAnimations();
      this.removeEventListener("click", this.handleClick);
      this.removeEventListener("keydown", this.handleKeydown);

      if (this.motionQuery?.removeEventListener) {
        this.motionQuery.removeEventListener("change", this.handleMotionChange);
      } else {
        this.motionQuery?.removeListener(this.handleMotionChange);
      }

      this.initialized = false;
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) return;

      const summary = event.target.closest(".formula-faq__summary");
      if (!summary || summary.closest("formula-faq-accordion") !== this) return;

      const item = summary.closest(".formula-faq__item");
      if (!item || this.motionQuery.matches || typeof item.animate !== "function") return;

      event.preventDefault();
      this.toggleItem(item, summary);
    }

    handleKeydown(event) {
      if (event.repeat || (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar")) return;
      if (!(event.target instanceof Element)) return;

      const summary = event.target.closest(".formula-faq__summary");
      if (!summary || summary.closest("formula-faq-accordion") !== this) return;

      const item = summary.closest(".formula-faq__item");
      if (!item || this.motionQuery.matches || typeof item.animate !== "function") return;

      event.preventDefault();
      this.toggleItem(item, summary);
    }

    toggleItem(item, summary) {
      const state = this.states.get(item);
      const shouldOpen = state === "closing" || (!item.open && state !== "opening");
      this.animateItem(item, summary, shouldOpen);
    }

    handleMotionChange() {
      if (this.motionQuery.matches) {
        this.settleAnimations();
      }
    }

    animateItem(item, summary, shouldOpen) {
      const currentHeight = item.getBoundingClientRect().height;
      this.cancelAnimation(item);

      if (shouldOpen) {
        item.open = true;
      }

      const borderHeight = item.offsetHeight - item.clientHeight;
      const targetHeight = shouldOpen ? item.offsetHeight : summary.offsetHeight + borderHeight;

      item.style.overflow = "hidden";
      item.classList.toggle("is-closing", !shouldOpen);
      this.states.set(item, shouldOpen ? "opening" : "closing");

      const animation = item.animate(
        {
          height: [`${currentHeight}px`, `${targetHeight}px`],
        },
        {
          duration: this.motionDuration,
          easing: "ease",
          fill: "both",
        },
      );

      this.animations.set(item, animation);
      animation.onfinish = () => {
        if (this.animations.get(item) !== animation) return;

        if (!shouldOpen) {
          item.open = false;
        }

        this.clearAnimation(item, animation);
      };
    }

    cancelAnimation(item) {
      const animation = this.animations.get(item);
      if (!animation) return;

      animation.onfinish = null;
      animation.cancel();
      this.animations.delete(item);
    }

    clearAnimation(item, animation) {
      animation.onfinish = null;
      animation.cancel();
      item.style.removeProperty("overflow");
      item.classList.remove("is-closing");
      this.animations.delete(item);
      this.states.delete(item);
    }

    settleAnimations() {
      this.animations.forEach((animation, item) => {
        const state = this.states.get(item);
        if (state === "closing") {
          item.open = false;
        } else if (state === "opening") {
          item.open = true;
        }

        this.clearAnimation(item, animation);
      });
    }

    get motionDuration() {
      const token = getComputedStyle(this).getPropertyValue("--m-duration-default").trim();
      const value = Number.parseFloat(token);

      if (!Number.isFinite(value)) return 250;
      return token.endsWith("ms") ? value : value * 1000;
    }
  }

  customElements.define("formula-faq-accordion", FormulaFaqAccordion);
}
